import React, { cloneElement, useCallback, useMemo, useState } from 'react';
import { mean, range, sum } from 'lodash';
import { UseVirtualizedStateOptions } from './use-virtualized-state-options';
import { VirtualizedState } from './virtualized-state';

/**
 * The default batch size to use.
 */
const DEFAULT_BATCH_SIZE = 10;

/**
 * Default index within the array of children to start rendering.
 */
const DEFAULT_START = 0;

/**
 * Default trigger threshold when a new batch of children should be rendered.
 */
const DEFAULT_THRESHOLD = 100;

/**
 * Default overscan value.
 */
const DEFAULT_OVERSCAN = 500;

/**
 * Helper function to compute the properties of a virtualized element.
 * @param direction The virtualized direction, horizontal or vertical.
 * @param computeOffset Callback to calculate the offset of a child.
 * @param element The element being virtualized.
 * @param start The start index of the virtualization.
 * @param index The index of this element.
 */
const computeChildProps = (
  direction: 'vertical' | 'horizontal',
  computeOffset: (start: number, end: number) => number | null,
  element: React.ReactElement,
  start: number,
  index: number,
): { style: React.CSSProperties } => {
  const offset = computeOffset(0, index + start);

  // If the measured offset is `null`, then don't apply special styling! This
  // is for the very-first render so we allow time to measure the initial
  // height of the first batch of rendered children.
  if (offset == null) {
    return { style: element.props.style };
  }

  if (direction === 'vertical') {
    return {
      style: {
        ...element.props.style,
        position: 'absolute',
        top: `${offset}px`,
        width: '100%',
      },
    };
  } else {
    return {
      style: {
        ...element.props.style,
        position: 'absolute',
        left: `${offset}px`,
        height: '100%',
      },
    };
  }
};

/**
 * React hook for tracking state in a virtualized component.
 * @param options Virtualized state options.
 */
export const useVirtualizedState = (
  children: React.ReactElement[],
  {
    batchSize = DEFAULT_BATCH_SIZE,
    initialBatchSize = batchSize,
    startIndex = DEFAULT_START,
    threshold = DEFAULT_THRESHOLD,
    overscan = DEFAULT_OVERSCAN,
  }: UseVirtualizedStateOptions,
): VirtualizedState => {
  // Current slice of children visible within the virtualized component. This
  // object indicates the slice of children that should be rendered to the
  // DOM inside the virtualized component.
  const [currentSlice, setCurrentSlice] = useState({
    start: startIndex || 0,
    end: initialBatchSize,
  });

  // Array of "sizes" of children. In a vertically oriented virtualized
  // component (like a list) this will be an array of child heights. In a
  // horizontally virtualized component, this is an array of widths.
  const [childSizes, setChildSizes] = useState<(number)[]>([]);

  // The average size of a child. We use this to "guess" the total scrollable
  // height or width of a virtualized component before we know what the
  // actual height of each child is. This allows us to avoid a render cycle for
  // each component just to measure its size.
  const meanChildSize = Math.round(mean(childSizes));

  // The guessed total size (height or width) of the virtualized scrollable
  // area.
  const size = sum(
    range(0, children.length).map(
      (i): number => childSizes[i] || meanChildSize,
    ),
  );

  // Helper function to calculate the size of a slice of the child heights. If
  // there are no measured child sizes yet, i.e. this is the first render, then
  // this function will return `null`.
  const computeOffset = useCallback(
    (start: number = 0, end: number = children.length): number | null => {
      if (childSizes.length === 0) {
        return null;
      }
      let accumulator = 0;
      for (let i = start; i < end; i++) {
        const curr = childSizes[i];
        accumulator += curr == null ? meanChildSize : curr;
      }
      return accumulator;
    },
    [children, childSizes, meanChildSize],
  );

  // Helper function to create a virtualized child.
  const createVirtualizedChild = useCallback(
    (child: React.ReactElement, index: number): React.ReactElement =>
      cloneElement(
        child,
        computeChildProps(
          'vertical',
          computeOffset,
          child,
          currentSlice.start,
          index,
        ),
      ),
    [computeOffset, currentSlice],
  );

  // Here we create the actual virtualized children that will be rendered to the
  // DOM by cloning them, then applying the necessary styles to position them
  // correctly.
  const virtualizedChildren = useMemo(
    (): React.ReactElement[] =>
      children
        .slice(currentSlice.start, currentSlice.end)
        .map(createVirtualizedChild),
    [currentSlice.start, currentSlice.end, childSizes, children],
  );

  return {
    batchSize,
    children,
    childSizes,
    currentSlice,
    initialBatchSize,
    meanChildSize,
    overscan,
    setChildSizes,
    setCurrentSlice,
    size,
    startIndex,
    threshold,
    virtualizedChildren,
  };
};
